import { useMutation, useQueryClient, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useEffect } from 'react';
import { imagePoolApiCalls } from '../apiCalls/imagePoolApiCalls';
import { IMAGE_VIDEO_POOL_QUERY_KEYS } from '../constants/constants';
import { PoolImage, ServerError } from '../typesAndInterfaces/typesAndInterfaces';
import { MB_InfinitePaginationPage } from '@/utils/general/util-hooks';

export const useUploadImage = () => {
  const queryClient = useQueryClient();

  return useMutation<PoolImage, ServerError, { formData: FormData; uploadProgress?: (percentCompleted: number) => void }, unknown>(
    async ({ formData, uploadProgress }) => imagePoolApiCalls.uploadImage({ formData, uploadProgress }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages]);
      },
    }
  );
};

export const useGetImagePoolImages = ({
  totalItemsPerPage,
  searchText,
  folderId,
  queryOptions,
}: {
  totalItemsPerPage: number;
  searchText?: string;
  folderId?: string;
  queryOptions?: UseInfiniteQueryOptions<MB_InfinitePaginationPage<PoolImage>, ServerError>;
}) => {
  const queryClient = useQueryClient();

  const searchQuery = searchText?.trim() === '' ? undefined : searchText;

  useEffect(() => {
    queryClient.invalidateQueries([IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages, folderId]);
  }, [folderId, queryClient, searchQuery]);

  return useInfiniteQuery<MB_InfinitePaginationPage<PoolImage>, ServerError>(
    [IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages, folderId, searchQuery],
    async ({ pageParam }: { pageParam?: string }) => imagePoolApiCalls.getImagePoolImages(totalItemsPerPage, pageParam, searchQuery, folderId),
    {
      refetchOnWindowFocus: false,
      getNextPageParam: lastPage => lastPage.nextOffset ?? undefined,
      ...queryOptions,
    }
  );
};

export const useEditImage = () => {
  const queryClient = useQueryClient();

  // folderId moves the image: a folder id, or '' to move it back to the root.
  return useMutation<string, ServerError, { imageId: string; name: string; folderId?: string }, unknown>(
    async ({ imageId, name, folderId }) => imagePoolApiCalls.editImage(imageId, name, folderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages]);
      },
    }
  );
};

export const useReorderImages = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ServerError, { imageId: string; order: number }[], unknown>(
    async imagesOrder => imagePoolApiCalls.reorderImages(imagesOrder),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages]);
      },
    }
  );
};

export const useDeleteImage = () => {
  const queryClient = useQueryClient();

  return useMutation<string, ServerError, string, unknown>(async imageId => imagePoolApiCalls.deleteImage(imageId), {
    onSuccess: () => {
      queryClient.invalidateQueries([IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages]);
    },
  });
};
